/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SystemModule
 */

import {
    Component,
    Input,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import { Scheduler } from 'ajs/modules/system/factories/scheduler.item.factory';
import { TItemConfig } from 'ajs/modules/data-model/factories/object-type-item.factory';
import * as l10n from './backup-overview-card.l10n';
import './backup-overview-card.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description Card component containing an overview of the configuration backup settings.
 * @author Alex Klyuev
 */

@Component({
    selector: 'backup-overview-card',
    templateUrl: './backup-overview-card.component.html',
})
export class BackupOverviewCardComponent {
    /**
     * Scheduler Item instance. Used to get backup configuration properties.
     */
    @Input()
    public scheduler: Scheduler;

    /**
     * Get keys from source bundles for template usage.
     */
    public readonly l10nKeys = l10nKeys;

    constructor(private readonly l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Getter for backup scheduler config object.
     */
    private get schedulerConfig(): TItemConfig {
        return this.scheduler.config;
    }

    /**
     * Getter for backup enabled property.
     */
    public get enabled(): boolean {
        return this.schedulerConfig.enabled;
    }

    /**
     * Shows the backup frequency.
     */
    public get frequency(): string {
        const {
            frequency,
            frequency_unit: frequencyUnit,
        } = this.schedulerConfig;

        const unit = frequencyUnit.enumeration('SCHEDULER_FREQUENCY_UNIT_').toLowerCase();

        return this.l10nService.getMessage(
            l10nKeys.frequencyStringLabel,
            [
                frequency,
                unit,
            ],
        );
    }

    /**
     * Shows the maximum number of backups.
     */
    public get maximumBackupsStored(): string | number {
        return this.schedulerConfig.backup_config_ref_data.maximum_backups_stored;
    }
}
